// Shared helpers for the cron-job "Days" field, which holds a cron day-of-week token:
// '*' (every day) or a comma list of single days and/or ASCENDING ranges, e.g. '5', '1-5', '1,3,5'.
// Days use the cron convention 0=Sun..6=Sat, with 7 also meaning Sunday (matches the "(0-7 or *)"
// UI label). This value must NEVER be run through amountToNumber() — that collapses '1-5' to 1
// (Monday only) and '1,3,5' to 135, which is what let cron jobs run every day (TKT-000490).

const DAY_LABEL = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const PART_RE = /^(\d)(?:-(\d))?$/;

// True when the token is a valid cron day-of-week value. Rejects out-of-range digits (8, 9),
// descending ranges ('6-2'), and malformed input ('1-', '1,,2', 'abc').
export const isValidCronDays = (v) => {
  const s = (v == null ? '' : v).toString().trim();
  if (s === '' || s === '*') return true;
  return s.split(',').every((raw) => {
    const m = PART_RE.exec(raw.trim());
    if (!m) return false;
    const a = Number(m[1]);
    if (a < 0 || a > 7) return false;
    if (m[2] === undefined) return true;
    const b = Number(m[2]);
    return b >= 0 && b <= 7 && a <= b; // ascending ranges only
  });
};

// Trim and default an empty value to '*'. Does NOT validate — pair with isValidCronDays.
export const normalizeCronDays = (v) => {
  const s = (v == null ? '' : v).toString().trim();
  return s === '' ? '*' : s;
};

// A Vuetify rule usable as :rules="[cronDaysRule]".
export const cronDaysRule = (v) => isValidCronDays(v)
  || 'Use *, a day 0-7, an ascending range like 1-5, or a list like 1,3,5';

const label = (n) => DAY_LABEL[Number(n) % 7]; // 7 -> 0 (Sunday)

// Human-readable summary for the job list, e.g. '*' -> 'Every day', '1-5' -> 'Mon–Fri',
// '1,3,5' -> 'Mon, Wed, Fri', '5' -> 'Fri'. Returns 'Invalid day' only for genuinely bad input.
export const describeCronDays = (v) => {
  const s = normalizeCronDays(v);
  if (s === '*') return 'Every day';
  if (!isValidCronDays(s)) return 'Invalid day';
  // A range/list that covers all seven weekdays reads better as 'Every day' (e.g. '0-7', '0-6').
  const covered = new Set();
  s.split(',').forEach((raw) => {
    const m = PART_RE.exec(raw.trim());
    const a = Number(m[1]);
    const b = m[2] === undefined ? a : Number(m[2]);
    for (let d = a; d <= b; d += 1) covered.add(d % 7);
  });
  if (covered.size === 7) return 'Every day';
  return s.split(',').map((raw) => {
    const m = PART_RE.exec(raw.trim());
    return m[2] === undefined ? label(m[1]) : `${label(m[1])}–${label(m[2])}`;
  }).join(', ');
};
